import { canonicalZone } from '@app/shared'
import { useMemo } from 'react'

/**
 * Every zone the platform knows, which is the IANA list itself — a hand-kept
 * list would be a second copy of something the runtime already holds, and would
 * be the one that goes stale.
 *
 * Each is offered under the one name the platform stores it by
 * (`canonicalZone`), once: a legacy spelling the runtime still lists is the
 * same zone, and offering it would save a name the server rewrites, leaving the
 * form and the record disagreeing about what was chosen. `current` is always
 * among them, so a stored zone shows as the answer rather than as a blank.
 *
 * `null` says the runtime holds no such list, which is a different answer from
 * an empty one: there is nothing to choose from, so the field asks for the name
 * instead of offering a picker with nothing in it.
 */
export function useZoneOptions(
  current?: string,
): { value: string; label: string }[] | null {
  return useMemo(() => {
    if (typeof Intl.supportedValuesOf !== 'function') return null
    const zones = new Set(Intl.supportedValuesOf('timeZone').map(canonicalZone))
    if (current) zones.add(canonicalZone(current))
    return [...zones].sort().map((zone) => ({
      value: zone,
      label: zoneLabel(zone),
    }))
  }, [current])
}

/** A zone as a person reads it: `America/New_York` is "America/New York". */
export const zoneLabel = (zone: string): string => zone.replace(/_/g, ' ')

/**
 * Whether a typed name is a zone at all.
 *
 * `Intl.DateTimeFormat` refuses a zone it cannot resolve, which is the one
 * check available where `supportedValuesOf` is not — the very runtime the typed
 * line is offered on. The name is canonicalized first, so a legacy spelling is
 * recognized here exactly as the server recognizes it.
 */
export function isZoneName(zone: string): boolean {
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: canonicalZone(zone) })
    return true
  } catch {
    return false
  }
}
